import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {translate} from "../../../translate/translate";
import {
    Box, Button, Checkbox, Chip,
    CircularProgress, Fab,
    FormControl, FormControlLabel,
    Grid, InputAdornment,
    InputLabel, ListItemText,
    MenuItem,
    Paper, Radio, RadioGroup, Switch, TextField,
    Typography
} from "@mui/material";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {Add, Download, Edit, FileCopyOutlined, Search} from "@mui/icons-material";
import {URLS} from "../../../services/app-urls";
import {PaginatedResult} from "../../../models/default/paginated-result";
import {ErrorComponent} from "../../../components/error/error.component";
import {Client} from "../../../models/basic/client";
import {sortListObj} from "../../../utils/list-utils";
import {ErrorMessage, Field, Form, Formik, FormikHelpers} from "formik";
import Select from "@mui/material/Select";
import TableActionComponent from "../../../components/table/table-action.component";
import CollapsibleTableComponent from "../../../components/table/collapsible-table.component";
import {RelatedCost, RelatedCostForm} from "../../../models/basic/related-cost";
import {toast} from "react-toastify";
import {AbstractService} from "../../../services/abstract-service";
import {RelatedCostFilter, RelatedCostService} from "../../../services/related-cost-service";
import {useFilters} from "../../../filters/base-filter";
import {usePagination} from "../../../utils/pagination";
import {orderByFn} from "../../../utils/order-by";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {enUS, ptBR} from "date-fns/locale";
import {ConvertDateToString} from "../../../components/date/date-timeFormat";
import {Project} from "../../../models/basic/project";
import {CostAllocationFilter, CostAllocationtService} from "../../../services/cost-allocation-service";
import {CostAllocation} from "../../../models/basic/cost-allocation";
import IconButton from "@mui/material/IconButton";
import {Category} from "../../../models/basic/category";
import GenericDialog from "../../../components/dialog/generic-dialog.component";
import {PoMonitoringService} from "../../../services/po-monitoring-service";
import {Material} from "../../../models/basic/material";
import {PoMonitoring} from "../../../models/basic/po-monitoring";


const CostAllocationProjectCategoryComponent = () => {
    const service = new CostAllocationtService();
    const serviceClient = new AbstractService<Client>(URLS.CLIENT);
    const serviceCategory = new AbstractService<Category>(URLS.CATEGORY);
    const serviceProject = new AbstractService<Project>(URLS.PROJECT);
    const servicePO = new PoMonitoringService();

    const {filters, updateFilterKeepingParam, updateFilter } = useFilters<CostAllocationFilter>(
        { project: "", client: "", ordering: "", category: "", initial_date: "", final_date: "", search: "" },
        ["project", "client", "ordering", "category", "initial_date", "final_date", "search"]
    );
    const [costAllocationList, setCostAllocationList] = React.useState<PaginatedResult<CostAllocation>>();
    const [orderDirection, setOrderDirection] = React.useState<'asc' | 'desc' | ''>('asc');

    const locale = translate.language == "pt" ? ptBR : enUS;
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [invalidDate, setInvalidDate] = useState<boolean>(false);

    const [selectedProject, setSelectedProject] = React.useState<number[]>([]);
    const [dataSourceProject, setDataSourceProject] = React.useState<Project[]>([]);

    const [selectedCategory, setSelectedCategory] = React.useState<number[]>([]);
    const [dataSourceCategory, setDataSourceCategory] = React.useState<Project[]>([]);

    const [materialList, setMaterialList] = React.useState<PaginatedResult<Material>>();
    const [openedDetail, setOpenedDetail] = React.useState<{ [id: number]: boolean }>({});

    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<CostAllocation>(costAllocationList);

    const {
        dataSource: dataSourceMaterial,
        pagination: paginationMaterial,
        resetPage: resetPageMaterial,
        handleChangePage: handleChangePageMaterial,
        handleChangeRowsPerPage: handleChangeRowsPerPageMaterial,
        resetDataSource: resetDataSourceMaterial
    } = usePagination<Material>(materialList);

    const expand = ["*"];

    const [dataSourceClient, setDataSourceClient] = React.useState<Client[]>([]);

    const [loading, setLoading] = React.useState(false);

    const [openDialog, setOpenDialog] = React.useState(false);

    const [loadingMaterial, setLoadingMaterial] = React.useState(false);
    const [selectedPO, setSelectedPO] = React.useState<PoMonitoring>();
  
    const [loadingDownload, setLoadingDownload] = React.useState(false);

    useEffect(() => {
        loadClients();
        loadProjects();
        loadCategories();
        initializeSelectedProjects();
        initializeSelectedCategories();
        search();
    }, []);

    useEffect(() => {
        if (!filters.project && dataSourceProject.length) {
            setSelectedProject(dataSourceProject.map((p: any) => p.id));
            updateFilterKeepingParam("project", dataSourceProject.map((p: any) => p.id).join(","));
        }
        if (!filters.category && dataSourceCategory.length) {
            setSelectedCategory(dataSourceCategory.map((p: any) => p.id));
            updateFilterKeepingParam("category", dataSourceCategory.map((p: any) => p.id).join(","));
        }
    }, [dataSourceProject, dataSourceCategory]);

    const initializeSelectedProjects = () => {
        if (filters.project) {
            const projects = filters.project.split(",");
            setSelectedProject(projects.map((p: any) => parseInt(p)));
        }
    }

    const initializeSelectedCategories = () => {
        if (filters.category) {
            const categories = filters.category.split(",");
            setSelectedCategory(categories.map((p: any) => parseInt(p)));
        }
    }

    const loadClients = () => {
        serviceClient.readAll().then(response => {
            const sortedData = sortListObj(response.data, "name");
            setDataSourceClient(sortedData);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const loadProjects = (idClient?: any) => {
        let filter;
        if (idClient && idClient !== "0") {
            filter = { client: idClient };
        }
        serviceProject.readAll(filter).then(response => {
            setDataSourceProject(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const loadCategories = (idClient?: any) => {
        serviceCategory.readAll({"active": true}).then(response => {
            const sortedData = sortListObj(response.data, "name");
            setDataSourceCategory(sortedData);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const search = (restartIndex = false) => {
        setLoading(true);
        service.readPaginated(filters, expand, pagination).then(response => {
            setCostAllocationList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        })

        restartIndex && resetPage();
        restartIndex && closeAllCollapses();
    }

    useEffect(() => {
        search();
    }, [pagination, filters.ordering]);

    useEffect(() => {
        if (selectedPO) {
            loadListMaterials(selectedPO.id);
        }
    }, [paginationMaterial]);

    const handleOpenDialog = (row: any) => {
        setOpenDialog(true);
        setSelectedPO(row);
        loadListMaterials(row.id);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const loadListMaterials = (id: number) => {
        setLoadingMaterial(true);
        servicePO.getPaginatedMaterials(id, undefined, paginationMaterial).then(response => {
            setMaterialList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
          setLoadingMaterial(false);
        });
    }

    const layoutDetail = (row: any) => {
        return (
            <TablePaginationComponent
                key={row.id}
                state={row.purchase_orders}
                displayedColumnsShow={["client", "nu_po", "projects", "origin", "created_date", "total_po", "value_by_project"]}
                displayedColumns={["client.name", "number", "projects", "origin", "purchase_date", "po_total", "project_total"]}
                numberColumns={["po_total", "project_total"]}
                currencyColumns={["po_total", "project_total"]}
                columnStyles={{1:"14%", 2:"14%", 3:"14%", 4:"14%", 5:"14%", 6: "14%", 7:"14%"}}
                dateColumns={["purchase_date"]}
                hideHeader={true}
                customColumns={["number", "projects"]}
                customCellRender={customDatailRender}
            />
        )
    }

    const handleDateChange = (key: "initial_date" | "final_date", date: any) => {
        if (date?.toString().includes("Invalid Date")) {
            setInvalidDate(true);
            return;
        }

        updateFilterKeepingParam(key, date ? ConvertDateToString(date) : "");
        key === "initial_date" ? setStartDate(date) : setEndDate(date);

        const compareDate = new Date(filters[key === "initial_date" ? "final_date" : "initial_date"] + "T12:00:00");
        setInvalidDate(date && compareDate && (key === "initial_date" ? date > compareDate : compareDate > date));
    };

    const handleStartDateChange = (date: any) => handleDateChange("initial_date", date);
    const handleEndDateChange = (date: any) => handleDateChange("final_date", date);

    const allProjectsSelected = () => {
        return selectedProject.length === dataSourceProject.length;
    }

    const allCategoriesSelected = () => {
        return selectedCategory.length === dataSourceCategory.length;
    }

    const renderSelection = (selected: any, dataSource: any[], allSelected: () => boolean) => {
        if (allSelected()) {
            return translate.t('all');
        }

        const sorted = selected
            .map((id: any) => dataSource.find(item => item.id === id)?.name)
            .filter(Boolean)
            .sort()

        return sorted.join(", ");
    };

    const renderProjects = (selected: any) => renderSelection(selected, dataSourceProject, allProjectsSelected);
    const renderCategories = (selected: any) => renderSelection(selected, dataSourceCategory, allCategoriesSelected);

    const customCellRender = (column: any, index: number, row: any) => {
        return ""
    }

    const customDatailRender = (column: any, index: number, row: any) => {
        if (column === "number") {
            return (
                <Box display="flex" alignItems="center">
                    <IconButton color="secondary" size="small"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    resetPageMaterial();
                                    resetDataSourceMaterial();
                                    setMaterialList(undefined);
                                    handleOpenDialog(row);
                                }}>
                        <FileCopyOutlined fontSize="small"/>
                    </IconButton>
                    <Typography fontWeight="bold" fontSize="small">
                        {row.number}
                    </Typography>
                </Box>
            )
        }
        if (column === "projects") {
            if (row.projects?.length) {
                return (
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Grid>
                            {row.projects.map((project: string, index: number) => (
                                <Chip key={index} size="small" style={{ marginRight: '5px', marginBottom: '5px' }} label={project}/>
                            ))}
                        </Grid>
                    </Box>
                )
            }
            return "-";
        }
        return ""
    }

    const openDetail = (row: any) => {
        setOpenedDetail({...openedDetail, [row.id]: !openedDetail[row.id]});
    }

    const closeAllCollapses = () => {
        setOpenedDetail({});
    }
  
    const export_xlsx = () => {
        search();
        setLoadingDownload(true);
        
        service.downloadWithExpands(filters, expand, 'export_xlsx').then(response => {
            service.downloadFile(response);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoadingDownload(false);
        });
    }

    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("report_cost_allocation_by_project_category")}
                </Typography>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                setTimeout(() => {
                                    search(true);
                                    setSubmitting(false);
                                }, 500);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Box alignItems="center" display="flex" justifyContent="space-between">
                                            <Grid alignItems="center" container spacing={2} direction={"row"}>
                                                <Grid size={4}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("client")}
                                                        </InputLabel>
                                                        <Select
                                                            name="client"
                                                            label={translate.t("client")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={filters.client || "0"}
                                                            onChange={(event) => {
                                                                loadProjects(event.target.value);
                                                                updateFilterKeepingParam("client", event.target.value as string)
                                                            }}
                                                        >
                                                            <MenuItem key="0" value="0">
                                                                {translate.t('all')}
                                                            </MenuItem>
                                                            {dataSourceClient?.map((client: Client) => (
                                                                <MenuItem key={client.id} value={client.id}>
                                                                    {client.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={4}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("project")}
                                                        </InputLabel>
                                                        <Select
                                                            name="project"
                                                            label={translate.t("project")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={selectedProject}
                                                            onChange={(event: any) => {
                                                                if (event.target.value.includes(0)) {
                                                                    if (!allProjectsSelected()) {
                                                                        setSelectedProject(dataSourceProject.map((p: any) => p.id));
                                                                        updateFilterKeepingParam("project", dataSourceProject.map((p: any) => p.id).join(","));
                                                                        return;
                                                                    }
                                                                    setSelectedProject([]);
                                                                    updateFilterKeepingParam("project", "");
                                                                    return;
                                                                }
                                                                setSelectedProject(event.target.value)
                                                                updateFilterKeepingParam("project", event.target.value.join(","));
                                                            }}
                                                            multiple
                                                            renderValue={(selected) => renderProjects(selected)}
                                                            MenuProps={{
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: '400px',
                                                                    },
                                                                },
                                                                disableAutoFocusItem: true,
                                                            }}
                                                            inputProps={{
                                                                sx: {
                                                                    maxWidth: '400px !important',
                                                                    width: '400px !important',
                                                                }
                                                            }}
                                                        >
                                                            <MenuItem key={0} value={0}>
                                                                <Checkbox checked={allProjectsSelected()}/>
                                                                <ListItemText primary={
                                                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                                                        <Typography variant="body1">{translate.t('all')}</Typography>
                                                                        {allProjectsSelected() && <Typography variant="body2"
                                                                                                                color="gray">{translate.t('clear')}</Typography>}
                                                                    </Box>
                                                                }/>
                                                            </MenuItem>
                                                            {dataSourceProject?.map((p: any) => (
                                                                <MenuItem key={p.id} value={p.id}>
                                                                    <Checkbox checked={selectedProject.includes(p.id)}/>
                                                                    <ListItemText primary={p.name}/>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={4}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("category")}
                                                        </InputLabel>
                                                        <Select
                                                            name="category"
                                                            label={translate.t("category")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={selectedCategory}
                                                            onChange={(event: any) => {
                                                                if (event.target.value.includes(0)) {
                                                                    if (!allCategoriesSelected()) {
                                                                        setSelectedCategory(dataSourceCategory.map((p: any) => p.id));
                                                                        updateFilterKeepingParam("category", dataSourceCategory.map((p: any) => p.id).join(","));
                                                                        return;
                                                                    }
                                                                    setSelectedCategory([]);
                                                                    updateFilterKeepingParam("category", "");
                                                                    return;
                                                                }
                                                                setSelectedCategory(event.target.value)
                                                                updateFilterKeepingParam("category", event.target.value.join(","));
                                                            }}
                                                            multiple
                                                            renderValue={(selected) => renderCategories(selected)}
                                                            MenuProps={{
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: '400px',
                                                                    },
                                                                },
                                                                disableAutoFocusItem: true,
                                                            }}
                                                            inputProps={{
                                                                sx: {
                                                                    maxWidth: '400px !important',
                                                                    width: '400px !important',
                                                                }
                                                            }}
                                                        >
                                                            <MenuItem key={0} value={0}>
                                                                <Checkbox checked={allCategoriesSelected()}/>
                                                                <ListItemText primary={
                                                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                                                        <Typography variant="body1">{translate.t('all')}</Typography>
                                                                        {allCategoriesSelected() && <Typography variant="body2"
                                                                                                              color="gray">{translate.t('clear')}</Typography>}
                                                                    </Box>
                                                                }/>
                                                            </MenuItem>
                                                            {dataSourceCategory?.map((p: any) => (
                                                                <MenuItem key={p.id} value={p.id}>
                                                                    <Checkbox checked={selectedCategory.includes(p.id)}/>
                                                                    <ListItemText primary={p.name}/>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={2}>
                                                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                                                        <DatePicker
                                                            label={translate.t("initial_date")}
                                                            value={startDate}
                                                            onChange={handleStartDateChange}
                                                            inputRef={(props: any) => (
                                                                <TextField
                                                                    {...props}
                                                                    required={false}
                                                                />
                                                            )}
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true,
                                                                    required: false,
                                                                }
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                </Grid>
                                                <Grid size={2}>
                                                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                                                        <DatePicker
                                                            label={translate.t("final_date")}
                                                            value={endDate}
                                                            onChange={handleEndDateChange}
                                                            inputRef={(props: any) => (
                                                                <TextField
                                                                    {...props}
                                                                    required={false}
                                                                />
                                                            )}
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true,
                                                                    required: false,
                                                                }
                                                            }}
                                                            minDate={new Date(filters.initial_date + "T12:00:00") || null}
                                                        />
                                                    </LocalizationProvider>
                                                </Grid>
                                                <Grid size={4}>
                                                    <Field
                                                        as={TextField}
                                                        name="search"
                                                        label={""}
                                                        fullWidth
                                                        variant={"outlined"}
                                                        autoFocus={true}
                                                        placeholder={translate.t("search_po_by_code")}
                                                        value={filters.search}
                                                        onChange={(event: React.ChangeEvent<{
                                                            value: unknown
                                                        }>) => updateFilterKeepingParam("search", event.target.value)}
                                                    />
                                                </Grid>
                                                <Grid size={1}>
                                                    <Fab
                                                        size="small"
                                                        type="submit"
                                                        color="secondary">
                                                        <Search/>
                                                    </Fab>
                                                </Grid>
                                                <Grid size={3} display="flex" justifyContent="end">
                                                    <Button
                                                        disabled={loadingDownload}
                                                        color="secondary"
                                                        variant="contained"
                                                        sx={{marginLeft: 'auto'}}
                                                        startIcon={<Download />}
                                                        onClick={() => {
                                                            export_xlsx();
                                                        }}>{translate.t("export").toUpperCase()}
                                                        {loadingDownload && <CircularProgress size="20px" sx={{marginLeft: '10px'}} />}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <CollapsibleTableComponent
                    state={dataSource}
                    displayedColumnsShow={["Client", "PO", "Platform", "Origin", "PO Date", "PO Total", "Value per project"]}
                    displayedColumns={["category.name", "nu_po", "projects", "origin", "created_date", "total", "total_per_project"]}
                    numberColumns={["total", "total_per_project"]}
                    currencyColumns={["total", "total_per_project"]}
                    orderByColumns={["category.name"]}
                    orderBy={filters.ordering}
                    orderByFn={(column: string, orderDirection: any) => {
                        console.log(column, orderDirection);
                        orderByFn(column, orderDirection, filters.ordering || "", setOrderDirection, updateFilterKeepingParam);
                    }}
                    orderDirection={orderDirection}
                    highlightedColumns={["category.name", "nu_po", "projects", "origin", "created_date", "total", "total_per_project"]}
                    highlightedColor={"#E5ECFF"}
                    highlightedHeader={false}
                    customColumns={["nu_po", "projects", "origin", "created_date", "total_per_project"]}
                    customCellRender={customCellRender}
                    columnStyles={{1:"14%", 2:"14%", 3:"14%", 4:"14%", 5:"14%", 6: "14%", 7:"14%"}}
                    actionsComponent={TableActionComponent}
                    childrenComponent={layoutDetail}
                    count={costAllocationList?.count}
                    next={costAllocationList?.next}
                    previous={costAllocationList?.previous}
                    page={pagination.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.limit}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 50, 100]}
                    loading={loading}
                    loadOnOpen={openDetail}
                    openControl={openedDetail}
                />
            </Paper>

            <GenericDialog
                open={openDialog}
                onClose={() => {
                    handleCloseDialog();
                }}
                title={"list_material_po"}
                colorButton={"primary"}
                hideConfirmButton={true}
                closeButtonText={"close"}
                maxWidth="lg">

                <Box display="flex" p={2}>
                    <Typography fontWeight="bold" width="50%">PO: {selectedPO?.number || '---'}</Typography>
                    <Typography fontWeight="bold" width="50%">{`Vendor: ${selectedPO?.supplier?.name || '---'}`}</Typography>
                </Box>

                <TablePaginationComponent
                    state={dataSourceMaterial}
                    displayedColumnsShow={["Material", "Description", "Qty", "Unit Net", "Total Net"]}
                    displayedColumns={["material_number", "material_description", "quantity", "unit_net_price", "total_net"]}
                    numberColumns={["unit_net_price", "total_net"]}
                    currencyColumns={["unit_net_price", "total_net"]}
                    count={materialList?.count}
                    next={materialList?.next}
                    previous={materialList?.previous}
                    page={paginationMaterial.page}
                    onPageChange={handleChangePageMaterial}
                    rowsPerPage={paginationMaterial.limit}
                    handleChangeRowsPerPage={handleChangeRowsPerPageMaterial}
                    rowsPerPageOptions={[10, 50, 100]}
                    loading={loadingMaterial}
                />

            </GenericDialog>

        </section>
    );
};

export default CostAllocationProjectCategoryComponent;
